import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Settings,
  Save,
  Upload,
  Loader2,
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Image,
  Clock,
  Server,
  Lock,
  TestTube,
  Send,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
} from "lucide-react";
import { AdminLayout } from "../../components/AdminLayout";
import adminApi from "../../lib/adminApi";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

const TIMEZONE_OPTIONS = [
  { value: "Atlantic/Canary", label: "GMT+0 - Canàries (WET)" },
  { value: "Europe/Madrid", label: "GMT+1 - Espanya peninsular (CET)" },
  { value: "Europe/Paris", label: "GMT+1 - França (CET)" },
  { value: "Europe/Berlin", label: "GMT+1 - Alemanya (CET)" },
  { value: "Europe/Rome", label: "GMT+1 - Itàlia (CET)" },
  { value: "Europe/Brussels", label: "GMT+1 - Bèlgica (CET)" },
  { value: "Europe/Amsterdam", label: "GMT+1 - Holanda (CET)" },
  { value: "Europe/Lisbon", label: "GMT+0 - Portugal (WET)" },
  { value: "Europe/London", label: "GMT+0 - Regne Unit (GMT)" },
  { value: "Europe/Athens", label: "GMT+2 - Grècia (EET)" },
  { value: "Europe/Bucharest", label: "GMT+2 - Romania (EET)" },
  { value: "Europe/Helsinki", label: "GMT+2 - Finlàndia (EET)" },
  { value: "America/Mexico_City", label: "GMT-6 - Mèxic (CST)" },
  { value: "America/Bogota", label: "GMT-5 - Colòmbia (COT)" },
  { value: "America/Argentina/Buenos_Aires", label: "GMT-3 - Argentina (ART)" },
  { value: "America/Santiago", label: "GMT-4 - Xile (CLT)" },
];

const EMAIL_PROVIDERS = [
  { value: "gmail", label: "Gmail", host: "smtp.gmail.com", port: 587 },
  { value: "outlook", label: "Outlook / Hotmail", host: "smtp-mail.outlook.com", port: 587 },
  { value: "yahoo", label: "Yahoo", host: "smtp.mail.yahoo.com", port: 587 },
  { value: "custom", label: "Personalitzat", host: "", port: 587 },
];

export default function AdminConfigPage() {
  const { t } = useTranslation();

  // Company config state
  const [config, setConfig] = useState({
    app_name: "VotaSegur",
    name: "",
    cif: "",
    address: "",
    postal_code: "",
    city: "",
    phone: "",
    email: "",
    website: "",
    logo_url: "",
    timezone: "Europe/Madrid",
    auto_dst: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [currentTime, setCurrentTime] = useState("");
  const fileInputRef = useRef(null);

  // Email config state
  const [emailConfig, setEmailConfig] = useState({
    provider: "gmail",
    email: "",
    password: "",
    sender_name: "VotaSegur",
    host: "smtp.gmail.com",
    port: 587,
    use_tls: true,
    use_ssl: false,
    enabled: true,
    smtp_username: "",
  });
  const [emailLoading, setEmailLoading] = useState(true);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [emailConfigured, setEmailConfigured] = useState(false);

  useEffect(() => {
    loadConfig();
    loadEmailConfig();
  }, []);

  // Live clock
  useEffect(() => {
    const updateClock = () => {
      try {
        const now = new Date();
        const formatted = now.toLocaleTimeString('ca-ES', {
          timeZone: config.timezone || 'Europe/Madrid',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
        setCurrentTime(formatted);
      } catch {
        setCurrentTime(new Date().toLocaleTimeString('ca-ES'));
      }
    };
    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, [config.timezone]);

  // Company config methods
  const loadConfig = async () => {
    try {
      const res = await adminApi.get("/admin/company-config");
      setConfig(res.data);
    } catch {
      toast.error(t('adminConfig.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.put("/admin/company-config", config);
      toast.success(t('adminConfig.savedSuccess'));
    } catch {
      toast.error(t('adminConfig.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t('adminConfig.imageMustBeImage'));
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await adminApi.post("/admin/company-logo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setConfig((prev) => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success(t('adminConfig.logoUploaded'));
    } catch {
      toast.error(t('adminConfig.logoUploadError'));
    } finally {
      setUploading(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  // Email config methods
  const loadEmailConfig = async () => {
    try {
      const res = await adminApi.get("/admin/email-config");
      if (res.data.configured) {
        setEmailConfig({ ...res.data, password: "" });
        setEmailConfigured(true);
      }
    } catch {
      toast.error(t('emailConfig.loadError'));
    } finally {
      setEmailLoading(false);
    }
  };

  const handleEmailProviderChange = (provider) => {
    const preset = EMAIL_PROVIDERS.find(p => p.value === provider);
    setEmailConfig(prev => ({
      ...prev,
      provider,
      host: preset?.host || prev.host,
      port: preset?.port || prev.port
    }));
  };

  const handleEmailSave = async () => {
    if (!emailConfig.email) {
      toast.error(t('emailConfig.fieldsRequired'));
      return;
    }
    if (!emailConfigured && !emailConfig.password) {
      toast.error(t('emailConfig.fieldsRequired'));
      return;
    }
    setEmailSaving(true);
    try {
      await adminApi.post("/admin/email-config", emailConfig);
      toast.success(t('emailConfig.configSaved'));
      setEmailConfigured(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('emailConfig.saveError'));
    } finally {
      setEmailSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setEmailTesting(true);
    setTestResult(null);
    try {
      const res = await adminApi.post("/admin/email-config/test", {});
      setTestResult(res.data);
      if (res.data.status === "success") {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('emailConfig.connectionError'));
      setTestResult({ status: "error", message: error.response?.data?.detail || t('admin.connectionError') });
    } finally {
      setEmailTesting(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error(t('emailConfig.enterDestEmail'));
      return;
    }
    setSendingTest(true);
    try {
      const res = await adminApi.post("/admin/email-config/send-test", { to_email: testEmail });
      if (res.data.status === "success") {
        toast.success(t('emailConfig.testEmailSent', { email: testEmail }));
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('emailConfig.sendError'));
    } finally {
      setSendingTest(false);
    }
  };

  if (loading || emailLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 md:p-8" data-testid="admin-config-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-white">
            {t('adminConfig.title')}
          </h1>
          <p className="text-slate-400 mt-1 text-sm">
            {t('adminConfig.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* LEFT COLUMN: Company Config */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-5"
          >
            {/* App Name */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-5">
              <Label className="text-sm text-slate-300 mb-1.5 flex items-center gap-2">
                <Settings className="h-4 w-4" /> {t('adminConfig.appNameLabel')}
              </Label>
              <Input
                value={config.app_name}
                onChange={(e) => handleChange("app_name", e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="VotaSegur"
                data-testid="app-name-input"
              />
              <p className="text-xs text-slate-500 mt-2">
                {t('adminConfig.appNameHint')}
              </p>
            </div>

            {/* Timezone */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-sm text-slate-300 flex items-center gap-2">
                  <Clock className="h-4 w-4" /> {t('adminConfig.timezoneLabel')}
                </Label>
                <div className="flex items-center gap-3 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2" data-testid="live-clock">
                  <Clock className="h-4 w-4 text-amber-400" />
                  <span className="text-lg font-mono font-semibold text-amber-400 tabular-nums tracking-wider">
                    {currentTime}
                  </span>
                </div>
              </div>
              <select
                value={config.timezone}
                onChange={(e) => handleChange("timezone", e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                data-testid="timezone-select"
              >
                {TIMEZONE_OPTIONS.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
              <div className="flex items-center gap-3 mt-3">
                <button
                  type="button"
                  role="switch"
                  aria-checked={config.auto_dst}
                  onClick={() => handleChange("auto_dst", !config.auto_dst)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${config.auto_dst ? 'bg-amber-500' : 'bg-slate-600'}`}
                  data-testid="auto-dst-switch"
                >
                  <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform`}
                    style={{ transform: config.auto_dst ? 'translateX(18px)' : 'translateX(2px)' }}
                  />
                </button>
                <span className="text-sm text-slate-400">{t('adminEntities2.autoSummerTime')}</span>
              </div>
            </div>

            {/* Logo */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-5">
              <Label className="text-sm text-slate-300 mb-3 block">{t('settings.logoLabel')}</Label>
              <div className="flex items-center gap-5">
                <div className="w-24 h-24 bg-slate-700 border border-slate-600 rounded-lg flex items-center justify-center overflow-hidden shrink-0">
                  {config.logo_url ? (
                    <img src={config.logo_url} alt="Logo" className="w-full h-full object-contain" data-testid="company-logo-preview" />
                  ) : (
                    <Image className="h-8 w-8 text-slate-500" />
                  )}
                </div>
                <div>
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                    data-testid="upload-logo-btn"
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    {t('adminConfig.uploadLogo')}
                  </Button>
                  <p className="text-xs text-slate-500 mt-2">{t('adminConfig.logoHint')}</p>
                </div>
              </div>
            </div>

            {/* Company Info */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 space-y-4">
              <div>
                <Label className="text-sm text-slate-300 mb-1.5 flex items-center gap-2">
                  <Building2 className="h-4 w-4" /> {t('adminConfig.companyName')}
                </Label>
                <Input
                  value={config.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder={t('adminConfig.companyNamePlaceholder')}
                  data-testid="company-name-input"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-300 mb-1.5 flex items-center gap-2">
                  <Building2 className="h-4 w-4" /> CIF
                </Label>
                <Input
                  value={config.cif}
                  onChange={(e) => handleChange("cif", e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="B12345678"
                  data-testid="company-cif-input"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-300 mb-1.5 flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> {t('adminConfig.addressLabel')}
                </Label>
                <Input
                  value={config.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder={t('adminConfig.addressPlaceholder')}
                  data-testid="company-address-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-300 mb-1.5 block">{t('settings.postalCodeLabel')}</Label>
                  <Input
                    value={config.postal_code}
                    onChange={(e) => handleChange("postal_code", e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="00000"
                    data-testid="company-postal-input"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-300 mb-1.5 block">{t('settings.cityLabel')}</Label>
                  <Input
                    value={config.city}
                    onChange={(e) => handleChange("city", e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder={t('adminConfig.localityPlaceholder')}
                    data-testid="company-city-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-300 mb-1.5 flex items-center gap-2">
                    <Phone className="h-4 w-4" /> {t('adminConfig.phoneLabel')}
                  </Label>
                  <Input
                    value={config.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="+34 000 000 000"
                    data-testid="company-phone-input"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-300 mb-1.5 flex items-center gap-2">
                    <Mail className="h-4 w-4" /> {t('adminConfig.emailLabel')}
                  </Label>
                  <Input
                    value={config.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="info@empresa.com"
                    data-testid="company-email-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm text-slate-300 mb-1.5 flex items-center gap-2">
                  <Globe className="h-4 w-4" /> Web
                </Label>
                <Input
                  value={config.website}
                  onChange={(e) => handleChange("website", e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="https://www.empresa.com"
                  data-testid="company-website-input"
                />
              </div>
            </div>

            {/* Save Company Config */}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-amber-600 hover:bg-amber-700"
              data-testid="save-config-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {t('adminConfig.saveConfig')}
            </Button>
          </motion.div>

          {/* RIGHT COLUMN: Email Config */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-5"
          >
            {/* Email Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Mail className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-heading font-semibold text-white">{t('adminEmail.title')}</h2>
                  <p className="text-xs text-slate-400">{t('adminEmail.smtpSubtitle')}</p>
                </div>
              </div>
              {emailConfigured && (
                <span className="flex items-center gap-1 text-sm text-green-400">
                  <CheckCircle2 className="h-4 w-4" />
                  {t('adminEmail.configured')}
                </span>
              )}
            </div>

            {/* Provider Selection */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Server className="h-4 w-4 text-blue-400" />
                {t('adminEmail.providerTitle')}
              </h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-slate-300 mb-1.5 text-sm">{t('adminEmail.selectProvider')}</Label>
                  <Select value={emailConfig.provider} onValueChange={handleEmailProviderChange}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EMAIL_PROVIDERS.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.value === "custom" ? t('adminConfig.smtpCustom') : p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {emailConfig.provider === "gmail" && (
                  <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-3">
                    <h4 className="text-xs font-medium text-blue-300 mb-1">{t('adminEmail.gmailInstructions')}</h4>
                    <ol className="text-xs text-blue-200 space-y-0.5 list-decimal list-inside">
                      <li>Ves a <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline">myaccount.google.com/apppasswords</a></li>
                      <li>{t('adminEmail.gmailStep2')}</li>
                      <li>{t('adminEmail.gmailStep3')}</li>
                      <li>{t('adminEmail.gmailStep4')}</li>
                    </ol>
                  </div>
                )}
              </div>
            </div>

            {/* Credentials */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Lock className="h-4 w-4 text-yellow-400" />
                {t('adminEmail.credentials')}
              </h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-slate-300 mb-1.5 text-sm">{t('adminEmail.emailLabel')}</Label>
                  <Input
                    type="email"
                    value={emailConfig.email}
                    onChange={(e) => setEmailConfig(prev => ({ ...prev, email: e.target.value }))}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="blockchain.tools.lleida@gmail.com"
                    data-testid="email-config-email"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 mb-1.5 text-sm">{t('adminEmail.appPassword')}</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={emailConfig.password}
                      onChange={(e) => setEmailConfig(prev => ({ ...prev, password: e.target.value }))}
                      className="bg-slate-700 border-slate-600 text-white pr-10"
                      placeholder={emailConfigured ? "••••••••••••••••" : t('admin.password16chars')}
                      data-testid="email-config-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {emailConfigured ? t('adminEmail.keepCurrent') : t('adminEmail.useAppPassword')}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-300 mb-1.5 text-sm">{t('adminEmail.senderName')}</Label>
                  <Input
                    value={emailConfig.sender_name}
                    onChange={(e) => setEmailConfig(prev => ({ ...prev, sender_name: e.target.value }))}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="VotaSegur"
                    data-testid="email-config-sender"
                  />
                </div>
              </div>
            </div>

            {/* Advanced SMTP (custom only) */}
            {emailConfig.provider === "custom" && (
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-5">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Settings className="h-4 w-4 text-purple-400" />
                  {t('adminEmail.advancedSmtp')}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 mb-1.5 text-sm">{t('adminEmail.hostSmtp')}</Label>
                    <Input
                      value={emailConfig.host}
                      onChange={(e) => setEmailConfig(prev => ({ ...prev, host: e.target.value }))}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="smtp.example.com"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 mb-1.5 text-sm">{t('adminEmail.port')}</Label>
                    <Input
                      type="number"
                      value={emailConfig.port}
                      onChange={(e) => setEmailConfig(prev => ({ ...prev, port: parseInt(e.target.value) }))}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="587"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-300 mb-1.5 text-sm">{t('adminEmail.smtpUsername')}</Label>
                    <Input
                      type="text"
                      value={emailConfig.smtp_username}
                      onChange={(e) => setEmailConfig(prev => ({ ...prev, smtp_username: e.target.value }))}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder={t('adminEmail.smtpUsernamePlaceholder')}
                      data-testid="smtp-username-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">{t('adminEmail.smtpUsernameHint')}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300 text-sm">{t('adminEmail.useTls')}</Label>
                    <Switch
                      checked={emailConfig.use_tls}
                      onCheckedChange={(checked) => setEmailConfig(prev => ({ ...prev, use_tls: checked }))}
                      className="data-[state=checked]:bg-emerald-500"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300 text-sm">{t('adminEmail.useSsl')}</Label>
                    <Switch
                      checked={emailConfig.use_ssl}
                      onCheckedChange={(checked) => setEmailConfig(prev => ({ ...prev, use_ssl: checked }))}
                      className="data-[state=checked]:bg-emerald-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Enable/Disable & Actions */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-white font-medium text-sm">{t('adminEmail.enableEmails')}</h3>
                  <p className="text-xs text-slate-400">{t('adminEmail.pauseEmails')}</p>
                </div>
                <Switch
                  checked={emailConfig.enabled}
                  onCheckedChange={(checked) => setEmailConfig(prev => ({ ...prev, enabled: checked }))}
                  className="data-[state=checked]:bg-emerald-500"
                />
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleEmailSave}
                  disabled={emailSaving}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="email-config-save"
                >
                  {emailSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  {t('adminEmail.saveConfig')}
                </Button>
                {emailConfigured && (
                  <Button
                    onClick={handleTestConnection}
                    disabled={emailTesting}
                    variant="outline"
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    {emailTesting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <TestTube className="h-4 w-4 mr-2" />}
                    {t('adminEmail.testConnection')}
                  </Button>
                )}
              </div>

              {testResult && (
                <div className={`mt-3 p-3 rounded-lg ${testResult.status === "success" ? "bg-green-900/30 border border-green-700" : "bg-red-900/30 border border-red-700"}`}>
                  <div className="flex items-center gap-2">
                    {testResult.status === "success" ? (
                      <CheckCircle2 className="h-5 w-5 text-green-400" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    )}
                    <span className={`text-sm ${testResult.status === "success" ? "text-green-300" : "text-red-300"}`}>
                      {testResult.message}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Send Test Email */}
            {emailConfigured && (
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-5">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Send className="h-4 w-4 text-green-400" />
                  {t('adminEmail.sendTestEmail')}
                </h3>
                <div className="flex gap-3">
                  <Input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white flex-1"
                    placeholder="destinatari@exemple.com"
                    data-testid="email-test-recipient"
                  />
                  <Button
                    onClick={handleSendTestEmail}
                    disabled={sendingTest || !testEmail}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="email-test-send"
                  >
                    {sendingTest ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    {t('adminEmail.send')}
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </AdminLayout>
  );
}
